import { getApiClient } from './api-client';
import { PROFILE_FIELDS_SERVICE_URL as servicePath } from '../constants/APIconstants';

/**
 * Per-org customizable profile fields (department / division / unit /
 * profession / role / levelOfTraining / specialty). Options are edited from the
 * Organizational Structure & Classifications settings page.
 *
 * Consumers should reach this through the hooks in
 * `shared/hooks/profile-fields.hooks`; direct consumption is only appropriate
 * inside `queryFn` / `mutationFn` bodies. Methods throw on failure (axios
 * default), return data directly, and accept `{ signal }` for cancellation.
 *
 * @typedef {'profession'|'role'|'levelOfTraining'|'department'|'division'|'specialty'|'unit'} ProfileFieldKey
 * @typedef {'ORGANIZATIONAL'|'PROFESSIONAL'} ProfileFieldGroup
 *
 * @typedef {Object} ProfileFieldOption
 * @property {string} _id
 * @property {string} code                 immutable, persisted on subscriptions
 * @property {string} label
 * @property {string|null} parentOptionId  → an option _id in another field
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 *
 * @typedef {Object} ProfileField
 * @property {string} id
 * @property {string} _id
 * @property {string} organizationId
 * @property {ProfileFieldKey} fieldKey
 * @property {ProfileFieldGroup} group
 * @property {string} displayLabel
 * @property {boolean} isMandatory
 * @property {number} order
 * @property {ProfileFieldOption[]} options
 *
 * `GET /profile-fields` returns fields grouped by `group`, sorted by `order`
 * within each group; options sorted alphabetically by `label` (server-side).
 * @typedef {Record<ProfileFieldGroup, ProfileField[]>} ProfileFieldsByGroup
 */

class ProfileFieldsService {
  /**
   * @param {{ signal?: AbortSignal }} [opts]
   * @returns {Promise<ProfileFieldsByGroup>}
   */
  async getProfileFields(opts) {
    const { profileFields } = await getApiClient().get(servicePath, opts);
    return profileFields;
  }

  /**
   * @param {{ fieldId: string, label: string, parentOptionId?: string|null }} args
   * @returns {Promise<ProfileField>}
   */
  async addOption({ fieldId, label, parentOptionId }) {
    const { field } = await getApiClient().post(`${servicePath}${fieldId}/options`, {
      label,
      ...(parentOptionId ? { parentOptionId } : {}),
    });
    return field;
  }

  /**
   * @param {{ fieldId: string, optionId: string, label: string }} args
   * @returns {Promise<ProfileField>}
   */
  async updateOption({ fieldId, optionId, label }) {
    const { field } = await getApiClient().put(`${servicePath}${fieldId}/options/${optionId}`, {
      label,
    });
    return field;
  }

  /**
   * Users affected by deleting the option. Scope `DEPENDENTS` (default): those whose
   * dependent values (role / level of training) would be cleared. Scope `HOLDERS`:
   * everyone holding the option. `total` is exact; `users` is one page, sorted by email.
   * @param {{ fieldId: string, optionId: string, scope?: 'DEPENDENTS'|'HOLDERS', page?: number, pageSize?: number }} args
   * @param {{ signal?: AbortSignal }} [opts]
   * @returns {Promise<{ total: number, page: number, pageSize: number, users: Array<{ userId: string|null, email: string|null, values: Record<string, string> }> }>}
   */
  async getOptionAffectedUsers({ fieldId, optionId, scope, page, pageSize }, opts) {
    return getApiClient().get(`${servicePath}${fieldId}/options/${optionId}/affected-users`, {
      ...opts,
      params: { scope, page, pageSize },
    });
  }

  /**
   * Delete an option, optionally reassigning affected subscriptions.
   * @param {{ fieldId: string, optionId: string, shouldReplace: boolean, replacementOptionId?: string }} args
   * @returns {Promise<ProfileField>}
   */
  async deleteOption({ fieldId, optionId, shouldReplace, replacementOptionId }) {
    const { field } = await getApiClient().post(
      `${servicePath}${fieldId}/options/${optionId}/delete`,
      { shouldReplace, ...(shouldReplace ? { replacementOptionId } : {}) },
    );
    return field;
  }
}

export default new ProfileFieldsService();
